import { ListsService } from './../../shared/services/lists.service';
import { LeadList } from './../../models/LeadList';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription } from 'rxjs';
import { AuthService } from './../../shared/services/auth.service';
import { ConfirmationService } from './../../shared/components/confirmation-modal/confirmation.service';
import { PaginatedResult } from './../../shared/model/paginated-result';
import { Router, ActivatedRoute } from '@angular/router';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { ToastService } from './../../shared/components/toast/toast.service';
import { CampaignService } from './../../shared/services/campaigns.service';
import { Campaign } from './../../models/Campaign';
import { CampaignFilter } from 'src/app/models/CampaignFilter';
import { Integration } from 'src/app/models/Integration';
import { IntegrationsService } from 'src/app/shared/services/integrations.service';
import { readTextPreference, saveTextPreference } from 'src/app/shared/utils/list-preferences';

/** Filtros da lista guardados no navegador (layout novo, 09/10/2026): voltar à tela não os perde. */
const PREF_STATUS = 'campaigns.status';
const PREF_INTEGRATION = 'campaigns.integration';
const PREF_LIST = 'campaigns.list';

@Component({
  templateUrl: './campaigns.component.html',
  styleUrls: ['./campaigns.component.scss']
})
export class CampaignsComponent implements OnInit, OnDestroy {

  name = '';
  page = 1;
  pageSize = 10;
  active: boolean;
  result: PaginatedResult<Campaign[]>;
  loading = false;
  /** A listagem falhou: a tela avisa e oferece de novo, em vez de dizer que não há automação. */
  loadError = false;

  lists$: Observable<LeadList[]>;
  subscriptions: Subscription[] = [];

  integrationId: string = "";
  integrations: Integration[];

  listId: string = "";
  lists: LeadList[];

  copyName = '';
  copyListId = '';
  campaign: Campaign;

  /** Segmento de status da lista: o valor de `active` de cada um. */
  readonly statusSegments: { key: string; label: string; value: boolean | null }[] = [
    { key: 'all', label: 'Todas', value: null },
    { key: 'active', label: 'Ativas', value: true },
    { key: 'inactive', label: 'Inativas', value: false },
  ];

  @ViewChild('modalCampaign') modal: NgbModalRef;

  /** Uma listagem por vez: um filtro novo cancela a resposta da anterior (regra 7). */
  private listSub?: Subscription;

  /** Opções dos filtros (integrações e listas), recarregadas ao trocar o usuário simulado. */
  private optionsSub?: Subscription;

  constructor(
    private router: Router,
    private modalService: NgbModal,
    private authService: AuthService,
    private listsService: ListsService,
    private activatedRoute: ActivatedRoute,
    private toastService: ToastService,
    private campaignService: CampaignService,
    private confirmationService: ConfirmationService,
    private integrationService: IntegrationsService
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(
      this.activatedRoute.queryParams.subscribe(params => {
        this.page = +params.page || 1;
        this.name = params.name || this.name;
        this.active = null;

        // Sem filtro na URL (tela aberta pelo menu), valem os filtros da última visita. Todo
        // filtro passa por `pageChanged`, que grava a preferência antes de ir para a URL: as duas
        // nunca discordam, e "Todas"/"Limpar" gravam vazio.
        const fromUrl = ['active', 'integration', 'list'].some(k => params[k] !== undefined);
        if (!fromUrl) {
          this.restorePreferences();
        } else {
          if (params.active) {
            this.active = JSON.parse(params.active);
          }
          this.integrationId = params.integration || '';
          this.listId = params.list || '';
        }

        this.listPaginated();
      }),

      // Simulando outro usuário: as opções dos filtros também são dele.
      this.authService.userChanged.subscribe(() => {
        // A escolha salva é do usuário efetivo: a do simulado vale, e a do admin fica intacta.
        this.restorePreferences();
        this.loadFilterOptions();
        this.listPaginated();
      })
    )
    this.loadFilterOptions();
  }

  private loadFilterOptions(): void {
    this.optionsSub?.unsubscribe();
    this.optionsSub = new Subscription();
    this.optionsSub.add(this.integrationService.listAll().subscribe(integrations => {
      this.integrations = integrations;
      this.dropStalePreference('integrationId', PREF_INTEGRATION, integrations);
    }));
    this.optionsSub.add(this.listsService.listAll().subscribe(lists => {
      this.lists = lists;
      this.dropStalePreference('listId', PREF_LIST, lists);
    }));
  }

  /**
   * Filtros salvos por usuário efetivo (o simulado, quando há simulação): o admin simulando um
   * cliente não apaga nem herda os filtros dele — review de 09/10 14:30, como em Conversas.
   */
  private preferenceKey(base: string): string {
    const userId = this.authService.getUser?.()?.id;
    return userId ? `${base}.${userId}` : base;
  }

  private restorePreferences(): void {
    const status = readTextPreference(this.preferenceKey(PREF_STATUS));
    this.active = status === 'true' ? true : status === 'false' ? false : null;
    this.integrationId = readTextPreference(this.preferenceKey(PREF_INTEGRATION));
    this.listId = readTextPreference(this.preferenceKey(PREF_LIST));
  }

  private savePreferences(): void {
    saveTextPreference(this.preferenceKey(PREF_STATUS), this.active === null || this.active === undefined ? '' : String(this.active));
    saveTextPreference(this.preferenceKey(PREF_INTEGRATION), this.integrationId || '');
    saveTextPreference(this.preferenceKey(PREF_LIST), this.listId || '');
  }

  /** O filtro salvo aponta para integração/lista que não existe mais: volta para "Todas". */
  private dropStalePreference(field: 'integrationId' | 'listId', key: string, options: { id: string }[]): void {
    const value = this[field];
    if (!value || !options || options.some(o => String(o.id) === String(value))) {
      return;
    }
    this[field] = '';
    saveTextPreference(this.preferenceKey(key), '');
    this.pageChanged(1);
  }

  get hasFilters(): boolean {
    return this.active !== null && this.active !== undefined || !!this.integrationId || !!this.listId || !!this.name;
  }

  clearFilters(): void {
    this.name = '';
    this.active = null;
    this.integrationId = '';
    this.listId = '';
    this.pageChanged(1);
  }

  isStatus(value: boolean | null): boolean {
    return (this.active ?? null) === value;
  }

  /** A lista da automação, pelo id (a listagem da API não traz o objeto da lista). */
  listName(campaign: Campaign): string {
    return this.lists?.find(l => String(l.id) === String(campaign.lead_list_id))?.name || campaign.lead_list?.name || '';
  }

  stepsCount(campaign: Campaign): number {
    return campaign.sequences?.length || 0;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.listSub?.unsubscribe();
    this.optionsSub?.unsubscribe();
  }

  listPaginated(): void {
    const filter: CampaignFilter = {
      name: this.name,
      integration: this.integrationId,
      list: this.listId,
      active: this.active
    }

    this.loading = true;
    this.loadError = false;
    this.listSub?.unsubscribe();
    this.listSub = this.campaignService.listPaginated(this.page, this.pageSize, filter).subscribe({
      next: result => {
        this.loading = false;
        this.result = result;
        if (result.last_page < this.page) {
          this.pageChanged(result.last_page);
        }
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  filterKeyPress(event: KeyboardEvent): void {
    const key = event?.key?.toLocaleUpperCase();
    if (key === 'ENTER' || !this.name) {
      this.pageChanged(this.page);
    }
  }

  filterByStatus(active: boolean = null) {
    this.active = active;
    this.pageChanged(this.page);
  }

  toggleActive(campaign: Campaign): void {
    campaign.active = !campaign.active;

    const successMessage = campaign.active
      ? 'Automação ativada com sucesso!'
      : 'Automação desativada com sucesso!';

    // Falhou: o switch volta, senão mostraria desligada uma automação que continua enviando.
    // send_to_all: false fixo (CU-17tm466mg5z) — o campo segue no corpo para o backend anterior
    // a esta versão, que exige send_to_all no PUT; aqui o objeto vem da listagem, sem o form.
    this.subscriptions.push(
      this.campaignService.save({ ...campaign, send_to_all: false } as Campaign).subscribe({
        next: () => this.toastService.success(successMessage),
        error: () => {
          campaign.active = !campaign.active;
          this.toastService.error('Não foi possível alterar a automação. Tente de novo.');
        },
      })
    )
  }

  pageChanged(page: any): void {
    this.savePreferences();
    let queryParams = {
      page,
      per_page: this.pageSize,
      name: this.name,
      active: this.active,
      integration: this.integrationId,
      list: this.listId
    };

    if (!this.name) {
      delete queryParams.name;
    }

    if (this.active === null) {
      delete queryParams.active;
    }

    if (this.integrationId === null || this.integrationId === '') {
      delete queryParams.integration
    }

    if (this.listId === null || this.listId === '') {
      delete queryParams.list
    }

    this.router.navigate(['/campaigns'], { queryParams });
  }

  confirmDelete(campaign: Campaign): void {
    this.confirmationService.show(`Deseja realmente excluir a automação [${campaign.name}]?`, () => this.delete(campaign.id));
  }

  delete(id: string): void {
    this.subscriptions.push(
      this.campaignService.delete(id).subscribe(
        () => {
          this.listPaginated();
          this.toastService.success('Automação excluída com sucesso!');
        },
        () => this.toastService.error('Erro ao excluir automação. Tente novamente mais tarde!')
      )
    );
  }

  showCopy(campaign: Campaign) {
    this.copyName = `Cópia de ${campaign.name}`;
    this.copyListId = '';
    this.lists$ = this.listsService.listAll();
    this.modalService.open(this.modal);
    this.campaign = campaign;
  }

  copy(modal: any): void {
    if (!this.copyName && !this.copyListId) {
      this.toastService.error('Preencha o nome e selecione a lista');
      return;
    }

    if (!this.copyName) {
      this.toastService.error('Preencha o nome');
      return;
    }

    if (!this.copyListId) {
      this.toastService.error('Selecione o a lista');
      return;
    }

    this.subscriptions.push(
      this.campaignService.copy(this.campaign.id, this.copyName, this.copyListId)
        .subscribe({
          next: () => {
            this.toastService.success('Automação copiada com sucesso!');
            this.listPaginated();
            modal.close();
          },
          error: err => {
            this.toastService.error(err.error.message);
          }
        })
    );
  }

}
